import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import RecipeModal from '../components/RecipeModal';
import RecipeDetail from '../components/RecipeDetail';
import { toast } from '../components/Toast';

export default function Recipes({ openRecipeId, onRecipeOpened }) {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [detailRecipe, setDetailRecipe] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filterCategory) params.set('category_id', filterCategory);
    const [r, c] = await Promise.all([
      api.get(`/recipes?${params}`),
      api.get('/recipe-categories'),
    ]);
    setRecipes(r);
    setCategories(c);
    setLoading(false);
  }, [search, filterCategory]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openRecipeId) return;
    api.get(`/recipes/${openRecipeId}`)
      .then(r => { setDetailRecipe(r); onRecipeOpened?.(); })
      .catch(() => {});
  }, [openRecipeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRandom = async () => {
    setFabOpen(false);
    try {
      const r = await api.get('/recipes/random');
      setDetailRecipe(r);
    } catch {
      toast('Geen recepten beschikbaar', 'error');
    }
  };

  const handleNewRecipe = () => {
    setFabOpen(false);
    setEditingRecipe(null);
    setShowModal(true);
  };

  const handleCardClick = async (recipe) => {
    try {
      const full = await api.get(`/recipes/${recipe.id}`);
      setDetailRecipe(full);
    } catch {
      toast('Kon recept niet laden', 'error');
    }
  };

  const handleEdit = (recipe) => {
    setDetailRecipe(null);
    setEditingRecipe(recipe);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Recept definitief verwijderen?')) return;
    try {
      await api.delete(`/recipes/${id}`);
      setDetailRecipe(null);
      load();
      toast('Recept verwijderd');
    } catch {
      toast('Fout bij verwijderen', 'error');
    }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditingRecipe(null);
    load();
    toast('Recept opgeslagen ✓');
  };

  return (
    <>
      {/* ── Sticky search toolbar ── */}
      <div className="topbar">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            placeholder="Zoeken op naam..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', color: 'var(--md-on-surface-variant)', cursor: 'pointer', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="content">
        {/* Category filter pills */}
        <div className="filter-row">
          <button
            className={`filter-pill ${!filterCategory ? 'active' : ''}`}
            onClick={() => setFilterCategory(null)}
          >
            Alle
          </button>
          {categories.map(c => (
            <button
              key={c.id}
              className={`filter-pill ${filterCategory === c.id ? 'active' : ''}`}
              onClick={() => setFilterCategory(filterCategory === c.id ? null : c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Recipe grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><span className="loading-spinner" /></div>
        ) : recipes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🍽️</div>
            <p>{search || filterCategory ? 'Geen recepten gevonden.' : 'Nog geen recepten. Voeg je eerste recept toe!'}</p>
          </div>
        ) : (
          <div className="recipe-grid">
            {recipes.map(r => (
              <div key={r.id} className="recipe-card" onClick={() => handleCardClick(r)}>
                {r.image_url ? (
                  <div className="recipe-img" style={{ backgroundImage: `url(${r.image_url})` }} />
                ) : (
                  <div className="recipe-img recipe-img-placeholder">🍽️</div>
                )}
                <div className="recipe-card-body">
                  <div className="recipe-card-name">{r.name}</div>
                  {r.rating > 0 && (
                    <div className="recipe-stars-sm">
                      {[1,2,3,4,5].map(s => (
                        <span key={s} className={s <= r.rating ? 'star-filled' : 'star-empty'}>★</span>
                      ))}
                    </div>
                  )}
                  {r.categories?.length > 0 && (
                    <div className="recipe-cat-row">
                      {r.categories.map(c => (
                        <span key={c.id} className="recipe-cat-tag">{c.name}</span>
                      ))}
                    </div>
                  )}
                  <div className="recipe-card-meta">
                    {r.prep_time > 0 && <span>⏱ {r.prep_time} min voorber.</span>}
                    {r.cook_time > 0 && <span>🔥 {r.cook_time} min koken</span>}
                    {r.servings > 0 && <span>👥 {r.servings} pers.</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Speed Dial FAB (Google Calendar style) ── */}
      {fabOpen && (
        <div className="fab-backdrop" onClick={() => setFabOpen(false)} />
      )}
      <div className="fab-container">
        {/* Dial items — animate in when open */}
        <div className={`fab-dial-items ${fabOpen ? 'open' : ''}`}>
          {/* Option 1: Willekeurig recept */}
          <div className="fab-dial-row">
            <button className="fab-dial-label" onClick={handleRandom}>
              Willekeurig recept
            </button>
            <button className="fab-mini" onClick={handleRandom} title="Willekeurig recept">
              🎲
            </button>
          </div>
          {/* Option 2: Nieuw recept */}
          <div className="fab-dial-row">
            <button className="fab-dial-label" onClick={handleNewRecipe}>
              Nieuw recept
            </button>
            <button className="fab-mini" onClick={handleNewRecipe} title="Nieuw recept">
              ✏️
            </button>
          </div>
        </div>

        {/* Main FAB — rotates to ✕ when open */}
        <button
          className={`fab ${fabOpen ? 'open' : ''}`}
          onClick={() => setFabOpen(o => !o)}
          aria-label={fabOpen ? 'Sluiten' : 'Opties'}
          aria-expanded={fabOpen}
        >
          <i className="fab-main-icon">＋</i>
        </button>
      </div>

      {showModal && (
        <RecipeModal
          recipe={editingRecipe}
          categories={categories}
          onClose={() => { setShowModal(false); setEditingRecipe(null); }}
          onSaved={handleSaved}
        />
      )}

      {detailRecipe && (
        <RecipeDetail
          recipe={detailRecipe}
          onClose={() => setDetailRecipe(null)}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}
